/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import {
  getMAUs,
  MAUsParams,
  MAUsResponse,
  getUniqueCustomers,
  UniqueCustomersParams,
  UniqueCustomersResponse,
} from './api'
import { MetricCard } from '../../components/MetricCard'

const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

interface FunnelGeneralProps {
  period: {
    month: number
    year: number
  }
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const FunnelGeneral: React.FC<FunnelGeneralProps> = ({ period, trigger, onLoaded }) => {
  const [mausData, setMausData] = useState<number>(0)
  const [totalBase, setTotalBase] = useState<number>(0)
  const [mausLoading, setMausLoading] = useState(false)
  const [mausError, setMausError] = useState<string | null>(null)
  const [mausCall] = useApiCall<MAUsParams, MAUsResponse>(getMAUs)

  // Track loading state
  useLoadingTracker({ isLoading: mausLoading, componentId: 'FunnelGeneral' })
  const [uniqueCustomersData, setUniqueCustomersData] = useState<number>(0)
  const [uniqueCustomersLoading, setUniqueCustomersLoading] = useState(false)
  const [uniqueCustomersError, setUniqueCustomersError] = useState<string | null>(null)
  const [uniqueCustomersCall] = useApiCall<UniqueCustomersParams, UniqueCustomersResponse>(
    getUniqueCustomers,
  )
  const [isDataLoaded, setIsDataLoaded] = useState(false)

  const fetchMAUsData = async () => {
    try {
      setMausLoading(true)
      setMausError(null)

      // Convert period format to endDate (end of month)
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')

      const response = await mausCall({
        endDate,
      })

      if (response.data) {
        setMausData(response.data.count)
      }
    } catch (err) {
      setMausError('Error al cargar los datos de MAUs')
      console.error('Error fetching MAUs:', err)
    } finally {
      setMausLoading(false)
    }
  }

  const fetchUniqueCustomersData = async () => {
    try {
      setUniqueCustomersLoading(true)
      setUniqueCustomersError(null)

      // Convert period format to endDate (end of month)
      const endDate = moment()
        .year(period.year)
        .month(period.month)
        .endOf('month')
        .format('YYYY-MM-DD')

      const response = await uniqueCustomersCall({
        endDate,
      })

      if (response.data) {
        setUniqueCustomersData(response.data.activeUsers)
        setTotalBase(response.data.totalBase)
      }
    } catch (err) {
      setUniqueCustomersError('Error al cargar los datos de clientes únicos')
      console.error('Error fetching unique customers:', err)
    } finally {
      setUniqueCustomersLoading(false)
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      setIsDataLoaded(false)
      Promise.all([fetchMAUsData(), fetchUniqueCustomersData()])
        .then(() => {
          // Pequeño delay para permitir que la transición se vea
          setTimeout(() => {
            setIsDataLoaded(true)
            if (onLoaded) onLoaded()
          }, 100)
        })
        .catch(() => {
          // En caso de error igual avanzar la cadena de loading
          if (onLoaded) onLoaded()
          setIsDataLoaded(true)
        })
    }
  }, [trigger, period])

  const mainMetrics = [
    {
      title: 'Base total',
      value: uniqueCustomersLoading ? 0 : totalBase,
    },
    {
      title: 'MAUs',
      value: mausLoading ? 0 : mausData,
    },
    {
      title: 'Clientes únicos',
      value: uniqueCustomersLoading ? 0 : uniqueCustomersData,
    },
  ]

  const funnelData = [
    {
      label: 'Base total',
      value: 100,
      color: '#C9D296',
    },
    {
      label: 'MAUs',
      value:
        mausLoading || totalBase === 0 ? 0 : parseFloat(((mausData / totalBase) * 100).toFixed(2)),
      color: '#7CA982',
    },
    {
      label: 'Usuarios activos',
      value:
        uniqueCustomersLoading || totalBase === 0
          ? 0
          : parseFloat(((uniqueCustomersData / totalBase) * 100).toFixed(2)),
      color: '#26342E',
    },
  ]

  if (mausError) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <span className="font-bold text-base block mb-4" style={montserratStyle}>
          Conversión - Funnel general
        </span>
        <div className="text-red-500 text-center py-8" style={montserratStyle}>
          {mausError}
        </div>
      </div>
    )
  }

  if (uniqueCustomersError) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <span className="font-bold text-base block mb-4" style={montserratStyle}>
          Conversión - Funnel general
        </span>
        <div className="text-red-500 text-center py-8" style={montserratStyle}>
          {uniqueCustomersError}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <span className="font-bold text-base block mb-4" style={montserratStyle}>
        Conversión - Funnel general
      </span>
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {mainMetrics.map(metric => (
          <MetricCard
            key={metric.title}
            title={metric.title}
            value={metric.value}
            showComparison
            className="bg-white rounded-xl shadow p-6"
          />
        ))}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="p-2 rounded border border-gray-300 hover:bg-gray-100 transition"
          aria-label="Descargar CSV"
        >
          <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
            />
          </svg>
        </button>
      </div>
      {/* Funnel */}
      <div className="relative mb-8">
        <div className="flex flex-col gap-4 mt-4">
          {funnelData.map((item, index) => (
            <div key={item.label} className="flex items-center justify-between">
              <div
                style={{
                  ...montserratStyle,
                  background: item.color,
                  height: 60,
                  maxWidth: '100%',
                  width: isDataLoaded ? `${item.value >= 100 ? 100 : item.value}%` : '0%',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                  paddingLeft: 16,
                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                  transitionDelay: `${index * 0.1}s`,
                }}
              >
                {item.label}
              </div>
              <div className="flex items-center ml-4" style={montserratStyle}>
                <span
                  className="text-lg font-bold mr-2"
                  style={{
                    color: '#1E1E1E',
                    opacity: isDataLoaded ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                    transitionDelay: `${index * 0.1 + 0.3}s`,
                  }}
                >
                  {item.value}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default FunnelGeneral
